import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cn } from "@/utils/cn"

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  showShimmer?: boolean;
}

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, showShimmer = false, ...props }, ref) => {
  const [shimmerProgress, setShimmerProgress] = React.useState(0);
  const isStartingUp = showShimmer && (value === 0 || value === undefined);

  // Animate shimmer progress from 0 to 15% over 20 seconds when starting up
  React.useEffect(() => {
    if (isStartingUp) {
      setShimmerProgress(0);
      const startTime = Date.now();
      const duration = 20000; // 20 seconds
      const targetProgress = 15; // End at 15%

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * targetProgress, targetProgress);
        setShimmerProgress(progress);

        if (progress >= targetProgress) {
          clearInterval(interval);
        }
      }, 100); // Update every 100ms for smooth animation

      return () => clearInterval(interval);
    } else {
      setShimmerProgress(0);
    }
  }, [isStartingUp]);

  // Use at least 5% width to ensure the shimmer is always visible when starting
  const displayValue = isStartingUp
    ? Math.max(5, shimmerProgress)
    : Math.max(0, value || 0);

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-4 w-full overflow-hidden rounded-full bg-slate-200",
        className
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="h-full w-full flex-1 transition-all duration-300 bg-blue-600"
        style={{
          transform: `translateX(-${100 - displayValue}%)`
        }}
      >
        {/* Shimmer overlay when enabled */}
        {showShimmer && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer bg-[length:200%_100%]" />
        )}
      </ProgressPrimitive.Indicator>
    </ProgressPrimitive.Root>
  );
})
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }